import {
  HttpMethod,
  HeadersType,
  Header,
  IExternalRequester,
  SendRequestParametersType,
  Parser,
  RequestType,
  KameleoonResponseType,
  KameleoonUtils,
  KameleoonLogger,
  IUrlProvider,
  IEventManager,
  HttpRequestType,
} from '@kameleoon/javascript-sdk-core';
import { RequestParametersType, CredentialsType } from './types';
import {
  AccessTokenSource,
  AccessTokenResponseType,
} from './accessTokenSource';
import { DEFAULT_REQUEST_TIMEOUT } from './constants';

// --- Note ---
// `RequesterMiddleware` is a decorator for `Requester` class to add
// authorization layer to Node.js SDK
export class RequesterMiddleware implements IExternalRequester {
  private readonly requester: IExternalRequester;
  private readonly urlProvider: IUrlProvider;
  private readonly accessTokenSource: AccessTokenSource;
  private readonly _isKameleoonRequester: boolean = false;
  private eventManager?: IEventManager;

  /**
   * @param requestTimeout - timeout of an access token request, in milliseconds
   */
  constructor(
    requester: IExternalRequester,
    credentials: CredentialsType,
    urlProvider: IUrlProvider,
    requestTimeout: number = DEFAULT_REQUEST_TIMEOUT,
  ) {
    this.requester = requester;
    this.urlProvider = urlProvider;
    this.accessTokenSource = new AccessTokenSource(
      credentials,
      (basicAuthToken, signal) => this.fetchAccessToken(basicAuthToken, signal),
      requestTimeout,
    );

    this._isKameleoonRequester = 'isKameleoonRequester' in requester;
  }

  get isKameleoonRequester(): boolean {
    return this._isKameleoonRequester;
  }

  /**
   * Sets the SDK event manager used to fire `EventType.HttpRequest` SDK events
   * for access token requests
   */
  public setEventManager(eventManager: IEventManager): void {
    this.eventManager = eventManager;
  }

  public async sendRequest<T extends RequestType>({
    url,
    parameters,
    retryCount,
    requestType,
  }: SendRequestParametersType<T>): Promise<KameleoonResponseType> {
    let updatedParameters = parameters;
    let accessToken: string | null = null;
    const needAuth =
      requestType === RequestType.Tracking ||
      requestType === RequestType.RemoteData;

    if (needAuth) {
      accessToken = await this.accessTokenSource.getToken(parameters.signal);

      if (accessToken) {
        updatedParameters = {
          ...parameters,
          headers: this.getAuthHeader(parameters, accessToken),
        };
      }
    }

    if (requestType === RequestType.Tracking) {
      let response = await this.requester.sendRequest({
        url,
        parameters: updatedParameters,
        retryCount,
        requestType,
      });

      if (response.status === 401) {
        // --- Note ---
        // Auth error - retry with new token
        this.accessTokenSource.discardToken(accessToken);

        // --- Note ---
        // If this is the last retry with `401` status
        // we try once more without authorization
        if (retryCount === 2) {
          response = await this.requester.sendRequest({
            url,
            parameters,
            requestType,
            retryCount,
          });
        }

        return response;
      }

      // --- Note ---
      // Forbidden (bot filtering) - simulate successful response from utils
      // to indicate that the bot was filtered
      if (response.status === 403) {
        return KameleoonUtils.simulateSuccessRequest<RequestType.Tracking>(
          requestType,
          null,
        );
      }

      return response;
    }

    return await this.requester.sendRequest({
      url,
      parameters: updatedParameters,
      requestType,
      retryCount,
    });
  }

  private async fetchAccessToken(
    basicAuthToken: string,
    signal: AbortSignal,
  ): Promise<AccessTokenResponseType> {
    const headers = {
      [Header.Authorization]: basicAuthToken,
      [Header.ContentType]: 'application/x-www-form-urlencoded',
    };
    const url = this.urlProvider.getAccessTokenUrl();

    KameleoonLogger.debug(() => {
      return `Running access token request Method: ${
        HttpMethod.Post
      }, Url: ${url}, with authorization token: ${Parser.parseSecret(
        basicAuthToken,
      )}`;
    });

    const parameters = {
      method: HttpMethod.Post,
      signal,
      headers,
      body: `grant_type=client_credentials`,
    } as const;

    const startTime = Date.now();

    let response: KameleoonResponseType;
    try {
      response = await this.requester.sendRequest({
        url,
        requestType: RequestType.Tracking,
        parameters,
      });
    } catch (error) {
      this.eventManager?.fireHttpRequest({
        requestType: HttpRequestType.AccessToken,
        duration: Date.now() - startTime,
        error,
      });

      throw error;
    }

    this.eventManager?.fireHttpRequest({
      requestType: HttpRequestType.AccessToken,
      duration: Date.now() - startTime,
      response,
    });

    KameleoonLogger.debug(() => {
      return `Received access token response: status: ${
        response.status
      } for request Method: ${
        HttpMethod.Post
      }, Url: ${url}, with authorization token: ${Parser.parseSecret(
        basicAuthToken,
      )}`;
    });

    if (!response.ok) {
      throw new Error(`Access token response status: ${response.status}`);
    }

    const json = await response.json();

    if (typeof json?.access_token !== 'string') {
      throw new Error(`Access token JSON response has no 'access_token' field`);
    }

    if (typeof json.expires_in !== 'number') {
      throw new Error(`Access token JSON response has no 'expires_in' field`);
    }

    return { accessToken: json.access_token, expiresIn: json.expires_in };
  }

  private getAuthHeader(
    parameters: RequestParametersType,
    accessToken: string,
  ): HeadersType {
    const headers =
      'headers' in parameters ? (parameters.headers as HeadersType) : {};

    return {
      ...headers,
      [Header.Authorization]: `Bearer ${accessToken}`,
    };
  }
}
